import type { Client } from "@archinventory/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Empty, Page } from "../../components/page";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/dialog";
import { Input, Label, Textarea } from "../../components/ui/input";
import { Table, TBody, Td, THead, Th, Tr } from "../../components/ui/table";
import { api, qk, unwrap } from "../../lib/api";
import { toastError, useCan } from "../../lib/hooks";

export const Route = createFileRoute("/_app/clients")({ component: Clients });

type ClientForm = {
  name: string;
  address: string;
  contactName: string;
  contactEmail: string;
  notes: string;
};

function Clients() {
  const qc = useQueryClient();
  const canManage = useCan("admin");
  const [editing, setEditing] = useState<Client | "new" | null>(null);
  const q = useQuery({ queryKey: qk.clients, queryFn: () => unwrap(api.clients.$get()) });

  const save = useMutation({
    mutationFn: async (v: ClientForm) => {
      if (editing === "new") return unwrap(api.clients.$post({ json: v }));
      if (editing) return unwrap(api.clients[":id"].$patch({ param: { id: editing.id }, json: v }));
    },
    onSuccess: async () => {
      setEditing(null);
      await qc.invalidateQueries({ queryKey: qk.clients });
      await qc.invalidateQueries({ queryKey: qk.projects });
    },
    onError: toastError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.clients[":id"].$delete({ param: { id } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.clients }),
    onError: toastError,
  });

  const current = editing && editing !== "new" ? editing : null;
  return (
    <Page
      title="Clients"
      subtitle="Companies your office inventories for."
      actions={
        canManage && (
          <Button onClick={() => setEditing("new")}>
            <Plus /> New client
          </Button>
        )
      }
    >
      {q.data?.clients.length === 0 && <Empty>No clients yet.</Empty>}
      {!!q.data?.clients.length && (
        <Table>
          <THead>
            <Tr>
              <Th>Name</Th>
              <Th>Address</Th>
              <Th>Contact</Th>
              <Th className="w-24" />
            </Tr>
          </THead>
          <TBody>
            {q.data.clients.map((c) => (
              <Tr key={c.id}>
                <Td className="font-medium">{c.name}</Td>
                <Td className="text-muted-foreground">{c.address}</Td>
                <Td className="text-muted-foreground">
                  {[c.contactName, c.contactEmail].filter(Boolean).join(" · ")}
                </Td>
                <Td className="text-right">
                  {canManage && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit"
                        onClick={() => setEditing(c)}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete"
                        onClick={() =>
                          confirm(`Delete ${c.name} and all its projects?`) && remove.mutate(c.id)
                        }
                      >
                        <Trash2 />
                      </Button>
                    </>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={current ? "Edit client" : "New client"}
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const v = (k: string) => String(f.get(k) ?? "");
            save.mutate({
              name: v("name"),
              address: v("address"),
              contactName: v("contactName"),
              contactEmail: v("contactEmail"),
              notes: v("notes"),
            });
          }}
        >
          <Label htmlFor="c-name">Name</Label>
          <Input id="c-name" name="name" defaultValue={current?.name} required />
          <Label htmlFor="c-address">Address</Label>
          <Input id="c-address" name="address" defaultValue={current?.address ?? ""} />
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="c-cn">Contact name</Label>
              <Input id="c-cn" name="contactName" defaultValue={current?.contactName ?? ""} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="c-ce">Contact email</Label>
              <Input
                id="c-ce"
                name="contactEmail"
                type="email"
                defaultValue={current?.contactEmail ?? ""}
              />
            </div>
          </div>
          <Label htmlFor="c-notes">Notes</Label>
          <Textarea id="c-notes" name="notes" defaultValue={current?.notes ?? ""} />
          <Button type="submit" disabled={save.isPending}>
            Save
          </Button>
        </form>
      </Dialog>
    </Page>
  );
}
